import { useEffect, useState } from 'react';
import {
  Building2, Calendar, DollarSign, Users, UserCheck, ShieldCheck, AlertTriangle, Clock,
} from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, StatCard, Badge } from '../components/ui';

type Stats = {
  activeClubs: number;
  pendingClubs: number;
  courts: number;
  activeCoaches: number;
  pendingCoachReviews: number;
  identityPending: number;
  bookings30d: number;
  lessons30d: number;
  grossRevenue30d: number;
  failedTransfers: number;
  refunds30d: number;
};

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    (async () => {
      const since = new Date();
      since.setDate(since.getDate() - 30);
      const sinceISO = since.toISOString();

      const [
        clubsActive, clubsPending, courtsActive,
        coachesActive, coachesPending, idPending,
        bookings30, lessons30,
        transfersFailed, refundsOpen,
      ] = await Promise.all([
        supabase.from('clubs').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('clubs').select('id', { count: 'exact', head: true }).eq('is_active', false),
        supabase.from('courts').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('coaches').select('id', { count: 'exact', head: true })
          .eq('is_active', true).eq('verification_status', 'verified'),
        supabase.from('coaches').select('id', { count: 'exact', head: true })
          .in('verification_status', ['pending_review', 'ai_reviewed']),
        supabase.from('identity_verifications').select('id', { count: 'exact', head: true })
          .in('status', ['pending_review', 'ai_reviewed']),
        supabase.from('court_bookings').select('total_amount', { count: 'exact' })
          .gte('created_at', sinceISO).in('status', ['confirmed', 'completed']),
        supabase.from('coach_bookings').select('total_amount', { count: 'exact' })
          .gte('created_at', sinceISO).in('status', ['confirmed', 'completed']),
        supabase.from('stripe_transfers').select('id', { count: 'exact', head: true })
          .in('status', ['failed', 'reversed']),
        supabase.from('payments').select('id', { count: 'exact', head: true })
          .in('status', ['refunded', 'partially_refunded'])
          .gte('updated_at', sinceISO),
      ]);

      const gross = (bookings30.data ?? []).reduce((s: number, b: any) => s + Number(b.total_amount ?? 0), 0)
                  + (lessons30.data  ?? []).reduce((s: number, b: any) => s + Number(b.total_amount ?? 0), 0);

      setStats({
        activeClubs:          clubsActive.count ?? 0,
        pendingClubs:         clubsPending.count ?? 0,
        courts:               courtsActive.count ?? 0,
        activeCoaches:        coachesActive.count ?? 0,
        pendingCoachReviews:  coachesPending.count ?? 0,
        identityPending:      idPending.count ?? 0,
        bookings30d:          bookings30.count ?? 0,
        lessons30d:           lessons30.count ?? 0,
        grossRevenue30d:      gross,
        failedTransfers:      transfersFailed.count ?? 0,
        refunds30d:           refundsOpen.count ?? 0,
      });
    })();
  }, []);

  const alerts: Array<{ label: string; count: number; tone: 'amber' | 'red'; href?: string }> = [];
  if (stats) {
    if (stats.pendingCoachReviews > 0) alerts.push({ label: 'Coach credentials awaiting review', count: stats.pendingCoachReviews, tone: 'amber', href: '/coaches' });
    if (stats.identityPending > 0)     alerts.push({ label: 'Identity verifications awaiting review', count: stats.identityPending, tone: 'amber', href: '/verifications' });
    if (stats.failedTransfers > 0)     alerts.push({ label: 'Stripe transfers failed or reversed', count: stats.failedTransfers, tone: 'red',   href: '/payouts' });
    if (stats.pendingClubs > 0)        alerts.push({ label: 'Clubs not yet live', count: stats.pendingClubs, tone: 'amber', href: '/clubs' });
  }

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Dashboard"
        subtitle="Platform snapshot for the last 30 days."
      />

      {alerts.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3 text-amber-900">
            <AlertTriangle className="w-4 h-4" />
            <div className="text-sm font-semibold">Needs attention</div>
          </div>
          <ul className="space-y-1">
            {alerts.map((a, i) => (
              <li key={i}>
                <a href={a.href}
                  className="flex items-center justify-between bg-white rounded-xl px-3 py-2 border border-amber-200 hover:bg-amber-50">
                  <span className="text-sm text-amber-900">{a.label}</span>
                  <Badge tone={a.tone}>{a.count}</Badge>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Building2}   label="Active clubs"    value={stats?.activeClubs} />
        <StatCard icon={Clock}       label="Active courts"   value={stats?.courts} />
        <StatCard icon={UserCheck}   label="Active coaches"  value={stats?.activeCoaches} />
        <StatCard icon={DollarSign}  label="Gross 30d"
                  value={stats ? `AED ${Math.round(stats.grossRevenue30d).toLocaleString()}` : undefined} />
      </div>

      <Section title="Bookings volume (30d)" subtitle="Confirmed and completed only.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <StatCard icon={Calendar} label="Court bookings"  value={stats?.bookings30d} />
          <StatCard icon={Users}    label="Coach lessons"   value={stats?.lessons30d} />
        </div>
      </Section>

      <Section title="Queue health" subtitle="Counts across review and payout queues.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard icon={ShieldCheck} label="ID verifications pending" value={stats?.identityPending} />
          <StatCard icon={UserCheck}   label="Coach credentials pending" value={stats?.pendingCoachReviews} />
          <StatCard icon={DollarSign}  label="Refunds (30d)" value={stats?.refunds30d} />
        </div>
      </Section>

      <SentrySmokeTest />
    </div>
  );
}

/**
 * Tiny guarded button that throws a synthetic error so we can verify
 * Sentry is wired up end-to-end. Only renders when the URL contains
 * ?sentry-test=1, so it never leaks to normal admin users.
 */
function SentrySmokeTest() {
  if (typeof window === 'undefined') return null;
  const show = new URLSearchParams(window.location.search).get('sentry-test') === '1';
  if (!show) return null;
  return (
    <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="text-sm font-medium text-amber-900 mb-1">Sentry smoke test</div>
      <p className="text-xs text-amber-900/80 mb-3">
        Clicking this throws an uncaught error, which should appear in
        Sentry within ~10 seconds. Only visible when <code>?sentry-test=1</code> is in the URL.
      </p>
      <button
        onClick={() => { throw new Error('Sentry smoke test at ' + new Date().toISOString()); }}
        className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm"
      >
        Break the world
      </button>
    </div>
  );
}
